import React, { useRef, useState, useEffect } from 'react'
import { ChevronDown, LogOut } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog"
import { Button } from '../ui/button';
import { DialogClose } from '@radix-ui/react-dialog';
import { Skeleton } from '../ui/skeleton';
import { useRouter } from 'next/navigation';
import { DASHBOARD_ROUTE } from '../../constants/pages-routes';
import { useSession } from '@/features/auth/context/session.context';
import { shortenName } from '../../lib/utils';
import { uploadProfilePicture } from "@/features/profile/api/profile.api";
import { getProfile } from "@/features/profile/api/profile.api";
import { deleteProfilePicture } from "@/features/profile/api/profile.api";
import successToast from "../../lib/toast/success.toast";
import errorToast from "../../lib/toast/error.toast";
import Image from 'next/image';

function UserAvatarDropdown() {

    const [isLogoutPopupOpen, setIsLogoutPopupOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false);
    const [isLoader, setLoader] = useState(false)
    const { profile, isLoading: sessionLoading, session, setProfile, logout } = useSession()
    const [firstName, lastName] = profile?.fullname.split(" ") || []
    const fileInputRef = useRef<HTMLInputElement>(null);

    // New state for profile picture update dialog
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const router = useRouter();

    useEffect(() => {
        if (!selectedFile) {
            setPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(selectedFile);
        setPreviewUrl(url);
        return () => {
            URL.revokeObjectURL(url);
        };
    }, [selectedFile]);
    const mobileName = profile?.fullname?.split(" ")?.[0] || profile?.fullname || ""
    const roleLabel =
        session?.role === "TeamLeader" ? "Team Lead" : (session?.role || "")

    // Handle file input change
    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setIsUpdateDialogOpen(true);
        }
    };

    // Handle cancel/confirm
    const handleCancel = () => {
        setIsUpdateDialogOpen(false);
        setSelectedFile(null);
    };
    const handleConfirm = async () => {

        if (!selectedFile || !profile?.id) {
            setIsUpdateDialogOpen(false);
            setSelectedFile(null);
            return;
        }
        setLoader(true)
        try {
            const result = await uploadProfilePicture(profile.id, selectedFile);
            if (result.success) {
                try {
                    const profileResult = await getProfile();
                    if (profileResult.success) {
                        setProfile(profileResult?.data);
                    }
                } catch (profileError) {
                    console.warn('Profile refresh failed, but upload succeeded:', profileError);
                }
                successToast('Profile picture updated successfully');
            } else {
                errorToast(result.message || 'Failed to update profile picture');
            }
        } catch (error) {
            setLoader(false)
            console.error('Error in handleConfirm:', error);
            errorToast('Failed to update profile picture');
        } finally {
            setLoader(false)
            setIsUpdateDialogOpen(false);
            setSelectedFile(null);
        }
    };

    const handleDelete = async () => {
        if (!profile?.id) {
            setIsDeleteDialogOpen(false);
            return;
        }
        setLoader(true)
        try {
            const result = await deleteProfilePicture(profile.id);
            if (result.success) {
                // Try to refresh profile data, but don't fail if it doesn't work
                try {
                    const profileResult = await getProfile();
                    if (profileResult.success) {
                        setProfile(profileResult.data);
                    }
                } catch (profileError) {
                    console.warn('Profile refresh failed, but delete succeeded:', profileError);
                }
                successToast('Profile picture deleted successfully');
            } else {
                errorToast(result.message || 'Failed to delete profile picture');
            }
        } catch (error) {
            setLoader(false)
            console.error('Error in handleDelete:', error);
            errorToast('Failed to delete profile picture');
        } finally {
            setLoader(false)
            setIsDeleteDialogOpen(false);
        }
    };

    const logoutClickHandler = async () => {
        try {
            setIsLoading(true)
            await logout()

        } finally {
            setIsLoading(false)
        }

    }

    const setIsLogoutPopupOpenHandler = (state: boolean) => {
        if (isLoading) {
            setIsLogoutPopupOpen(true)
            return
        }
        setIsLogoutPopupOpen(state)
    }

    if (sessionLoading) {
        return (
            <Avatar className='cursor-pointer rounded-none flex justify-center items-center w-auto max-w-full min-w-0 gap-2'>
                <div className="flex items-center pr-1 min-w-0">
                    <div className="flex flex-col items-end gap-1 min-w-0">
                        <span className='text-sm'><Skeleton className='w-16 h-2 sm:w-20' /></span>
                        <span className='text-muted-foreground text-xs'><Skeleton className='w-10 h-2 sm:w-8' /></span>
                    </div>

                </div>
                <div className='h-10 w-10 rounded-full overflow-hidden'>
                    <AvatarImage
                        key={profile?.profilePicture || 'no-image'}
                        src={profile?.profilePicture || undefined}
                        className='object-cover rounded-full object-top scale-125'
                    />
                    <AvatarFallback className='bg-muted text-sm'></AvatarFallback>
                </div>
                <ChevronDown className='relative w-5 md:w-5' />
            </Avatar>
        )
    }

    return (
        <>
            <Dialog open={isLogoutPopupOpen} onOpenChange={setIsLogoutPopupOpenHandler}>
                <DropdownMenu
                    open={isDropdownOpen}
                    onOpenChange={setIsDropdownOpen}
                    modal={false}
                >
                    <DropdownMenuTrigger className='h-auto'>
                        <Avatar className='cursor-pointer rounded-none flex justify-center items-center w-auto max-w-full min-w-0 gap-2'>
                            <div className="flex items-center pr-1 min-w-0">
                                <div className="flex flex-col items-end min-w-0">
                                    <span className='text-xs sm:text-sm font-medium leading-tight truncate max-w-[84px] sm:max-w-[140px] md:max-w-none md:overflow-visible md:whitespace-nowrap md:text-clip'>{profile?.fullname}</span>
                                    <span className='text-muted-foreground text-[10px] sm:text-xs leading-tight truncate max-w-[84px] sm:max-w-[140px]'>{roleLabel}</span>
                                </div>

                            </div>
                            <div className='h-10 w-10 rounded-full overflow-hidden'>
                                <AvatarImage
                                    key={profile?.profilePicture || 'no-image'}
                                    src={profile?.profilePicture || undefined}
                                    className='object-cover rounded-full object-top scale-125'
                                />
                                <AvatarFallback className='bg-muted text-sm'>{shortenName(firstName, lastName)}</AvatarFallback>
                            </div>
                            <ChevronDown className='relative w-5 md:w-5' />
                        </Avatar>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {/* profile link */}
                        <DropdownMenuItem
                            onSelect={(e) => {
                                e.preventDefault();
                                setIsDropdownOpen(false);
                                router.push(DASHBOARD_ROUTE.profile);
                            }}
                        >
                            Profile
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onSelect={e => {
                                e.preventDefault();
                                fileInputRef.current?.click();
                            }}
                        >
                            Update Profile Picture
                        </DropdownMenuItem>
                        {profile?.profilePicture && (
                            <DropdownMenuItem
                                onSelect={e => {
                                    e.preventDefault();
                                    setIsDeleteDialogOpen(true);
                                }}
                                className="text-destructive"
                            >
                                Delete Profile Picture
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuItem asChild>
                            <DialogTrigger className='flex items-center gap-3 w-full cursor-pointer'>
                                <LogOut className=" h-[1.2rem] w-[1.2rem] transition-all" />
                                Logout
                            </DialogTrigger>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>

                {/* Hidden file input for profile picture */}
                <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    tabIndex={-1}
                    onChange={handleFileSelect}
                />

                <DialogContent
                    onInteractOutside={(e) => {
                        if (isLoading) {
                            e.preventDefault()
                        }
                    }}
                    className='rounded-custom'
                >
                    <DialogTitle>
                        Logout?
                    </DialogTitle>
                    <DialogDescription>
                        Are you sure you want to logout?
                    </DialogDescription>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button
                                size={"sm"}
                                // className='outline-none'
                                variant={"outline"}
                            >
                                Cancel
                            </Button>
                        </DialogClose>
                        <Button
                            onClick={logoutClickHandler}
                            disabled={isLoading}
                            showLoader={true}
                            size={"sm"}
                        >
                            Log out
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog >

            {/* Confirmation dialog for profile picture update */}
            <Dialog open={isUpdateDialogOpen} onOpenChange={setIsUpdateDialogOpen}>
                <DialogContent>
                    <DialogTitle>Update Profile Picture</DialogTitle>
                    {selectedFile && (
                        <div className="flex flex-col items-center gap-4">
                            <Image
                                src={previewUrl || ''}
                                alt="Profile Preview"
                                width={128}
                                height={128}
                                className="rounded-full w-32 h-32 object-cover border"
                            />
                            <span>{selectedFile.name}</span>
                        </div>
                    )}
                    <DialogDescription>
                        Are you sure you want to update your profile picture?
                    </DialogDescription>
                    <DialogFooter>
                        <Button variant="outline" onClick={handleCancel} type="button">Cancel</Button>
                        <Button onClick={handleConfirm} disabled={isLoader ? true : false} type="button">Confirm</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirmation dialog for profile picture deletion */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent>
                    <DialogTitle>Delete Profile Picture</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to delete your profile picture? This action cannot be undone.
                    </DialogDescription>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)} type="button">
                            Cancel
                        </Button>
                        <Button variant="destructive" disabled={isLoader} onClick={handleDelete} type="button">
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}

export default UserAvatarDropdown